import { useState, useRef } from "react";
import { Upload, X, Loader2, CheckCircle2, MessageCircle, FileVideo, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { whatsappLink } from "@/config/site";
import { track } from "@/lib/analytics";

const PROPERTY_TYPES = ["Studio", "Apartment", "House", "Office or other"];
const CONDITIONS = ["Light cleaning", "Normal end-of-tenancy cleaning", "Very dirty", "Unsure"];
const CUSTOMER_TYPES = ["Tenant", "Homeowner", "Landlord", "Estate agent", "Property manager", "Business", "Other"];
const RESIDENTIAL_SERVICES = [
  "End of tenancy cleaning", "Move-out cleaning", "Deep cleaning", "Apartment cleaning",
  "House cleaning", "Oven cleaning", "Carpet cleaning", "Rubbish removal", "Furniture removal",
];
const COMMERCIAL_SERVICES = [
  "Office cleaning", "Retail cleaning", "Restaurant or café cleaning", "Salon or barbershop cleaning",
  "Gym cleaning", "Apartment common-area cleaning", "Other business-premises cleaning", "Recurring cleaning contract",
];
const YESNO = ["Yes", "No"];

const inputCls =
  "h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-emerald-600/20 focus-visible:border-emerald-600";

const Field = ({ label, required, children, testid }) => (
  <div className="flex flex-col gap-1.5" data-testid={testid}>
    <Label className="text-sm font-semibold text-slate-700">
      {label} {required && <span className="text-emerald-600">*</span>}
    </Label>
    {children}
  </div>
);

export const QuoteForm = ({ sourcePage = "/", prefillArea = "", prefillServices = [], prefillCustomerType = "" }) => {
  const emptyForm = {
    full_name: "", phone: "", email: "", customer_type: prefillCustomerType, property_location: prefillArea,
    property_type: "", bedrooms: "", bathrooms: "", is_empty: "", parking: "",
    preferred_date: "", preferred_time: "", property_condition: "", message: "",
    company: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [services, setServices] = useState(prefillServices);
  const [files, setFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const addFiles = (list) => {
    const arr = Array.from(list).filter((f) => f.size <= 60 * 1024 * 1024);
    if (arr.length !== list.length) toast.error("Some files exceed the 60MB limit and were skipped.");
    setFiles((prev) => [...prev, ...arr].slice(0, 10));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your name";
    if (!/^[+\d][\d\s()-]{5,}$/.test(form.phone.trim())) e.phone = "Enter a valid phone number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email";
    if (!form.property_location.trim()) e.property_location = "Enter the area or Eircode";
    if (!form.property_type) e.property_type = "Select a property type";
    if (!consent) e.consent = "Please tick the consent box";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildWaMessage = () => {
    const msg = `Hello, I would like a quote for end-of-tenancy cleaning. The property is located in ${form.property_location || "[AREA]"}. It has ${form.bedrooms || "[NUMBER]"} bedrooms and ${form.bathrooms || "[NUMBER]"} bathrooms. My preferred date is ${form.preferred_date || "[DATE]"}.`;
    return whatsappLink(msg);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data);
      }
      await api.post("/leads", {
        ...form, services, uploaded_files: uploaded, consent, source_page: sourcePage,
      });
      track.formSubmit(form.property_location);
      setDone(true);
      window.scrollTo({ top: document.getElementById("quote")?.offsetTop - 80 || 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Please try WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section id="quote" className="bg-emerald-50 py-20 sm:py-28" data-testid="quote-section">
        <div className="mx-auto max-w-2xl px-4 text-center sm:px-6">
          <div className="rounded-3xl border border-emerald-200 bg-white p-10 shadow-xl" data-testid="quote-success">
            <CheckCircle2 size={56} className="mx-auto text-emerald-600" />
            <h2 className="mt-5 font-display text-3xl font-black text-slate-900">Thank you</h2>
            <p className="mt-3 text-slate-600">
              We have received your request and will contact you shortly. For a faster quote, send
              your photos or video through WhatsApp.
            </p>
            <a
              href={buildWaMessage()} onClick={() => track.whatsappClick("quote-success")}
              target="_blank" rel="noreferrer" data-testid="continue-whatsapp-btn"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-base font-bold text-white transition-transform hover:scale-105"
            >
              <MessageCircle size={18} /> Continue on WhatsApp
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="quote" className="bg-emerald-50 py-20 sm:py-28" data-testid="quote-section">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <p className="text-sm font-bold uppercase tracking-widest text-emerald-700">Get a free quote</p>
          <h2 className="mt-3 font-display text-4xl font-black tracking-tight text-slate-900 sm:text-5xl">
            Request Your Quote
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Fill in the details below. Adding photos or a short video helps us confirm an exact price.
          </p>
        </div>

        <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-9" data-testid="quote-form" noValidate>
          {/* Honeypot */}
          <input type="text" name="company" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Full name" required testid="field-name">
              <Input data-testid="input-name" className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Your name" />
              {errors.full_name && <span className="text-xs text-red-500">{errors.full_name}</span>}
            </Field>
            <Field label="Phone number" required testid="field-phone">
              <Input data-testid="input-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="08X XXX XXXX" />
              {errors.phone && <span className="text-xs text-red-500">{errors.phone}</span>}
            </Field>
            <Field label="Email address" required testid="field-email">
              <Input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
              {errors.email && <span className="text-xs text-red-500">{errors.email}</span>}
            </Field>
            <Field label="Property location or Eircode" required testid="field-location">
              <Input data-testid="input-location" className={inputCls} value={form.property_location} onChange={(e) => set("property_location", e.target.value)} placeholder="e.g. Lucan / D15" />
              {errors.property_location && <span className="text-xs text-red-500">{errors.property_location}</span>}
            </Field>

            <Field label="Customer type" testid="field-customer-type">
              <Select value={form.customer_type} onValueChange={(v) => set("customer_type", v)}>
                <SelectTrigger data-testid="select-customer-type" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{CUSTOMER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>

            <Field label="Property type" required testid="field-type">
              <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                <SelectTrigger data-testid="select-type" className={inputCls}><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              {errors.property_type && <span className="text-xs text-red-500">{errors.property_type}</span>}
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Bedrooms" testid="field-bedrooms">
                <Input data-testid="input-bedrooms" className={inputCls} value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} placeholder="e.g. 2" inputMode="numeric" />
              </Field>
              <Field label="Bathrooms" testid="field-bathrooms">
                <Input data-testid="input-bathrooms" className={inputCls} value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} placeholder="e.g. 1" inputMode="numeric" />
              </Field>
            </div>

            <Field label="Is the property empty?" testid="field-empty">
              <Select value={form.is_empty} onValueChange={(v) => set("is_empty", v)}>
                <SelectTrigger data-testid="select-empty" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{YESNO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Is there parking available?" testid="field-parking">
              <Select value={form.parking} onValueChange={(v) => set("parking", v)}>
                <SelectTrigger data-testid="select-parking" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{YESNO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>

            <Field label="Preferred cleaning date" testid="field-date">
              <Input data-testid="input-date" type="date" className={inputCls} value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} />
            </Field>
            <Field label="Preferred time" testid="field-time">
              <Input data-testid="input-time" type="time" className={inputCls} value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} />
            </Field>

            <Field label="Property condition" testid="field-condition">
              <Select value={form.property_condition} onValueChange={(v) => set("property_condition", v)}>
                <SelectTrigger data-testid="select-condition" className={inputCls}><SelectValue placeholder="Select condition" /></SelectTrigger>
                <SelectContent>{CONDITIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <div className="mt-6">
            <Label className="text-sm font-semibold text-slate-700">Services required</Label>
            <p className="mt-1 text-xs font-bold uppercase tracking-wide text-emerald-700">Residential</p>
            <div className="mt-2 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {RESIDENTIAL_SERVICES.map((s) => (
                <label key={s} data-testid={`service-check-${s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors ${services.includes(s) ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
                  <Checkbox checked={services.includes(s)} onCheckedChange={() => toggleService(s)} className="data-[state=checked]:bg-emerald-700 data-[state=checked]:border-emerald-700" />
                  {s}
                </label>
              ))}
            </div>
            <p className="mt-4 text-xs font-bold uppercase tracking-wide text-emerald-700">Commercial</p>
            <div className="mt-2 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {COMMERCIAL_SERVICES.map((s) => (
                <label key={s} data-testid={`service-check-${s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors ${services.includes(s) ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
                  <Checkbox checked={services.includes(s)} onCheckedChange={() => toggleService(s)} className="data-[state=checked]:bg-emerald-700 data-[state=checked]:border-emerald-700" />
                  {s}
                </label>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <Field label="Message or additional information" testid="field-message">
              <Textarea data-testid="input-message" className="min-h-28 rounded-xl border-slate-200 bg-slate-50 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-emerald-600/20" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Anything else we should know (heavy dirt, mould, access, etc.)" />
            </Field>
          </div>

          <div className="mt-6">
            <Label className="text-sm font-semibold text-slate-700">Upload photos or video (optional)</Label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
              data-testid="upload-zone"
              className={`mt-3 flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors ${dragging ? "border-emerald-600 bg-emerald-50" : "border-slate-300 bg-slate-50 hover:border-emerald-400"}`}
            >
              <Upload size={26} className="text-emerald-600" />
              <p className="mt-2 text-sm font-semibold text-slate-700">Drag & drop or click to upload</p>
              <p className="text-xs text-slate-400">Images & video, up to 60MB each</p>
              <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" data-testid="file-input"
                onChange={(e) => addFiles(e.target.files)} />
            </div>
            {files.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="file-list">
                {files.map((f, i) => (
                  <span key={i} className="flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-800">
                    {f.type.startsWith("video") ? <FileVideo size={13} /> : <ImageIcon size={13} />}
                    {f.name.length > 20 ? f.name.slice(0, 18) + "…" : f.name}
                    <button type="button" onClick={(e) => { e.stopPropagation(); setFiles((p) => p.filter((_, idx) => idx !== i)); }} className="text-emerald-600 hover:text-red-500"><X size={13} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <label className="mt-6 flex items-start gap-3 text-sm text-slate-600" data-testid="consent-row">
            <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="consent-checkbox" className="mt-0.5 data-[state=checked]:bg-emerald-700 data-[state=checked]:border-emerald-700" />
            <span>I agree to be contacted regarding my quote request.</span>
          </label>
          {errors.consent && <span className="mt-1 block text-xs text-red-500">{errors.consent}</span>}

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <button type="submit" disabled={submitting} data-testid="submit-quote-btn"
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-emerald-700 px-8 py-4 text-base font-bold text-white transition-all hover:bg-emerald-800 disabled:opacity-60">
              {submitting ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : "Send My Request"}
            </button>
            <a href={buildWaMessage()} onClick={() => track.whatsappClick("quote-form")} target="_blank" rel="noreferrer" data-testid="quote-whatsapp-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#25D366] px-8 py-4 text-base font-bold text-[#128C7E] transition-colors hover:bg-[#25D366] hover:text-white">
              <MessageCircle size={18} /> Continue on WhatsApp
            </a>
          </div>
        </form>
      </div>
    </section>
  );
};
